import { FetchResult } from 'src/types/TApiQueries';
import {
  AiSettings,
  AiSettingsUpdatePayload,
  AiBudgetResponse,
  AiUserBudget,
  AiUserBudgetUpdatePayload,
  AiAggregateStats,
  AiTimeSeriesStats,
  AiCreditsResponse,
  AiCreditLedgerResponse,
  AiUsageBreakdownResponse,
} from 'src/types/TAiSettings';
import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from '../utils/helpers';

const baseUrl = config.hostUrl;

export async function getAiSettings(accessToken: string): Promise<FetchResult<AiSettings>> {
  return performFetchWithLogging(`${baseUrl}/api/v1/settings/ai`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function updateAiSettings(
  data: AiSettingsUpdatePayload,
  accessToken: string,
): Promise<FetchResult<AiSettings>> {
  return performFetchWithLogging(`${baseUrl}/api/v1/settings/ai`, {
    method: 'PUT',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

export async function getAiBudget(accessToken: string): Promise<FetchResult<AiBudgetResponse>> {
  return performFetchWithLogging(`${baseUrl}/api/v1/stats/ai/budget`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function getUserAiBudget(
  userId: number,
  accessToken: string,
): Promise<FetchResult<AiUserBudget>> {
  return performFetchWithLogging(`${baseUrl}/api/v1/users/${userId}/settings/ai/budget`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function updateUserAiBudget(
  userId: number,
  data: AiUserBudgetUpdatePayload,
  accessToken: string,
): Promise<FetchResult<AiUserBudget>> {
  return performFetchWithLogging(`${baseUrl}/api/v1/users/${userId}/settings/ai/budget`, {
    method: 'PUT',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

export async function getAiCredits(
  period: string,
  accessToken: string,
): Promise<FetchResult<AiCreditsResponse>> {
  const params = new URLSearchParams({ period });
  return performFetchWithLogging(`${baseUrl}/api/v1/stats/ai/credits?${params}`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function getAiStats(
  period: string,
  accessToken: string,
  userId?: string,
): Promise<FetchResult<AiAggregateStats>> {
  const params = new URLSearchParams({ period });
  if (userId) params.set('user_id', userId);
  return performFetchWithLogging(`${baseUrl}/api/v1/stats/ai?${params}`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function getAiCreditLedger(
  accessToken: string,
  opts?: { limit?: number; groupId?: number },
): Promise<FetchResult<AiCreditLedgerResponse>> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts?.groupId !== undefined) params.set('group_id', String(opts.groupId));
  const query = params.toString();
  const url = query
    ? `${baseUrl}/api/v1/stats/ai/ledger?${query}`
    : `${baseUrl}/api/v1/stats/ai/ledger`;
  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function getAiUsageBreakdown(
  accessToken: string,
  opts?: { groupId?: number; period?: number },
): Promise<FetchResult<AiUsageBreakdownResponse>> {
  const params = new URLSearchParams();
  if (opts?.groupId !== undefined) params.set('group_id', String(opts.groupId));
  if (opts?.period !== undefined) params.set('period', String(opts.period));
  const query = params.toString();
  const url = query
    ? `${baseUrl}/api/v1/stats/ai/usage-breakdown?${query}`
    : `${baseUrl}/api/v1/stats/ai/usage-breakdown`;
  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function getAiTimeSeriesStats(
  startTime: string,
  endTime: string,
  accessToken: string,
  opts?: { userId?: string; statSubtype?: string },
): Promise<FetchResult<AiTimeSeriesStats>> {
  const params = new URLSearchParams({ start_time: startTime, end_time: endTime });
  if (opts?.userId) params.set('user_id', opts.userId);
  if (opts?.statSubtype) params.set('stat_subtype', opts.statSubtype);
  return performFetchWithLogging(`${baseUrl}/api/v1/stats/timeseries/ai?${params}`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}
